
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/base/SEO';

export default function Features() {

  const features = [
    {
      icon: 'ri-line-chart-line',
      title: 'Advanced Analytics & Reporting',
      subtitle: 'Campaign Tracking & Competitor Insights',
      description: 'Monitor your campaign performance with detailed analytics and benchmark against competitors.',
      details: [
        'Real-time campaign performance tracking across all platforms',
        'Competitor analysis and success rate comparisons',
        'Performance analytics: detailed reach, engagement, and conversion metrics',
        'CRM analytics: track customer interactions and conversions',
        'Drive and monitor growth with actionable insights',
        'Campaign-by-campaign breakdown with ROI calculations',
        'Ability to keep track of expenses and budget allocation',
        'Custom date ranges and performance trend analysis',
        'Automated performance reports and insights',
        'Audience demographics and behavior analytics',
        'Export data to CSV, PDF, or integrate with BI tools',
      ],
      color: 'from-[#38a3a5] to-[#22577A]',
      bgColor: 'from-[#66b3ff]/20 to-[#38a3a5]/20',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Agentic AI Marketing Calendar',
      subtitle: 'Automatic Planning & Reports',
      description: 'Let our AI take the wheel with intelligent, autonomous planning that adapts to your brand and audience.',
      details: [
        'Automatically generates content calendars based on your goals and audience insights',
        'Predictive analytics to identify optimal posting times and content themes',
        'Real-time performance reports with actionable recommendations',
        'Continuous learning from your engagement metrics to improve future plans',
        'Seamless integration with your marketing strategy and campaigns',
      ],
      color: 'from-[#22577A] to-[#38a3a5]',
      bgColor: 'from-[#e4f4ff]/20 to-[#b3d9ff]/20',
    },
    {
      icon: 'ri-calendar-schedule-line',
      title: 'Post Planning & Scheduling',
      subtitle: 'Cross-Platform Management',
      description: 'Schedule and manage content across all major social media channels from one unified dashboard.',
      details: [
        'Support for Twitter/X, Facebook, Instagram, LinkedIn, TikTok, Pinterest, and more',
        'Visual content calendar with drag-and-drop scheduling',
        'Bulk scheduling: upload and schedule multiple posts at once',
        'Queue management with automatic posting',
        'Time zone optimization for global audiences',
        'Preview how posts will appear on each platform before publishing',
        'Publishing on multiple platforms simultaneously',
        'Scheduled posts with customizable timing per platform',
      ],
      color: 'from-[#38a3a5] to-[#66b3ff]',
      bgColor: 'from-[#b3d9ff]/20 to-[#e4f4ff]/20',
    },
    {
      icon: 'ri-quill-pen-line',
      title: 'AI-Driven Content Creation',
      subtitle: 'Create High-Quality Content Instantly',
      description: 'Generate compelling, high-quality content that resonates with your audience using advanced intelligent technology.',
      details: [
        'AI caption suggestions for every post type',
        'Natural language generation for authentic, engaging posts',
        'Create high-quality content optimized for each platform',
        'Content variations tailored to different platforms and audiences',
        'Image and video caption generation',
        'Tone and style matching to your brand voice',
        'SEO-optimized content for maximum discoverability',
        'Multi-language support for global reach',
      ],
      color: 'from-[#66b3ff] to-[#b3d9ff]',
      bgColor: 'from-[#e4f4ff]/20 to-[#b3d9ff]/20',
    },
    {
      icon: 'ri-magic-line',
      title: 'Smart Adaptation',
      subtitle: 'Platform-Optimized Content',
      description: 'Automatically optimize language, hashtags, and tone for each social media platform.',
      details: [
        'Platform-specific language optimization (e.g., casual for TikTok, professional for LinkedIn)',
        'Intelligent hashtag generation based on trending topics and your niche',
        'Character count optimization for platform limits',
        'Emoji and formatting suggestions for better engagement',
        'Audience segmentation for targeted messaging',
        'A/B testing capabilities to find what works best',
      ],
      color: 'from-[#b3d9ff] to-[#e4f4ff]',
      bgColor: 'from-[#66b3ff]/20 to-[#b3d9ff]/20',
    },
    {
      icon: 'ri-calendar-event-line',
      title: 'AI-Powered Event Planner',
      subtitle: 'For Blogs, Media, and Events',
      description: 'Plan and coordinate content around important dates, events, and industry moments.',
      details: [
        'Automatic detection of relevant holidays, events, and trending topics',
        'Custom event calendar integration',
        'Campaign planning around product launches and announcements',
        'Blog post scheduling coordinated with social media promotion',
        'Media kit preparation and distribution timeline',
        'Event countdown and reminder posts',
      ],
      color: 'from-[#e4f4ff] to-[#b3d9ff]',
      bgColor: 'from-[#b3d9ff]/20 to-[#66b3ff]/20',
    },
    {
      icon: 'ri-kanban-view',
      title: 'Kanban Board & Client Collaboration',
      subtitle: 'Seamless Team & Client Workflows',
      description: 'Visualize your content workflow and collaborate effortlessly with your team and clients.',
      details: [
        'Visual workflow management with customizable stages',
        'Drag-and-drop interface for easy content movement',
        'Collaborate with clients: add notations within content pages',
        'Shared accounts with client login access',
        'Multiple users per client page (customizable permissions)',
        'Team member assignments and notifications',
        'Comment threads and feedback loops',
        'Approval workflows for content review',
        'Activity tracking and audit logs',
        'Integration with project management tools',
      ],
      color: 'from-[#b3d9ff] to-[#66b3ff]',
      bgColor: 'from-[#e4f4ff]/20 to-[#b3d9ff]/20',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Post Playground',
      subtitle: 'AI Engine for Trending Content Analysis',
      description: 'Experiment with content ideas and analyze what\'s trending in real-time.',
      details: [
        'Real-time trend detection across social media platforms',
        'Content performance prediction before posting',
        'Competitor content analysis and benchmarking',
        'Viral content pattern recognition',
        'Engagement rate forecasting',
        'Content remix suggestions based on top performers',
        'Sentiment analysis for audience response prediction',
      ],
      color: 'from-[#66b3ff] to-[#38a3a5]',
      bgColor: 'from-[#b3d9ff]/20 to-[#e4f4ff]/20',
      link: '/playground',
      linkText: 'Explore The Playground',
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Visual Feed Planner',
      subtitle: 'Plan Your Perfect Instagram Grid',
      description: 'Design a clean and aesthetic feed with our moveable grid planner and split photo tools.',
      details: [
        'Moveable grid for planning a clean and aesthetic Instagram feed',
        'Drag-and-drop to rearrange order of posts',
        'Create any feed layout you like with visual preview',
        'Ability to see split photos before posting',
        'Split a big picture into multiple individual posts (grid posts)',
        'Automatic image cropping and sizing for perfect splits',
        'Preview how your feed will look before publishing',
        'Save multiple feed layout templates',
      ],
      color: 'from-[#e4f4ff] to-[#66b3ff]',
      bgColor: 'from-[#e4f4ff]/30 to-[#b3d9ff]/20',
    },
    {
      icon: 'ri-folder-image-line',
      title: 'Media Library & Asset Management',
      subtitle: 'Organize All Your Content',
      description: 'Store, organize, and manage all your media files in one centralized library.',
      details: [
        'Unlimited media library storage',
        'Support for images, videos, GIFs, and documents',
        'Smart tagging and categorization system',
        'Search by keywords, dates, or campaigns',
        'Bulk upload and import from cloud storage',
        'Team-wide access with permission controls',
        'Brand asset folders for logos, templates, and guidelines',
        'Quick media insertion into posts',
      ],
      color: 'from-[#b3d9ff] to-[#38a3a5]',
      bgColor: 'from-[#b3d9ff]/20 to-[#e4f4ff]/20',
    },
    {
      icon: 'ri-mail-send-line',
      title: 'Press Release & Email Marketing',
      subtitle: 'Reach Media & Customers Directly',
      description: 'Send press releases and email campaigns directly from the platform.',
      details: [
        'Send press releases to media contacts and journalists',
        'Email blast hosting with customizable templates',
        'Segment audiences for targeted campaigns',
        'Track email open rates and click-through rates',
        'Schedule email campaigns in advance',
        'Integration with popular email marketing tools',
        'A/B testing for email subject lines and content',
        'Automated follow-up sequences',
      ],
      color: 'from-[#66b3ff] to-[#22577A]',
      bgColor: 'from-[#66b3ff]/20 to-[#b3d9ff]/20',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Client Invoicing & Expense Tracking',
      subtitle: 'Manage Your Business Finances',
      description: 'Invoice clients and track expenses all in one place for streamlined business management.',
      details: [
        'Client invoicing with customizable invoice templates',
        'Automatic invoice generation based on services',
        'Track payments and outstanding balances',
        'Ability to keep track of expenses by campaign or client',
        'Expense categorization and reporting',
        'Integrate with accounting software (QuickBooks, Xero)',
        'Tax-ready financial reports',
        'Multi-currency support for global clients',
      ],
      color: 'from-[#38a3a5] to-[#b3d9ff]',
      bgColor: 'from-[#38a3a5]/20 to-[#66b3ff]/20',
    },
    {
      icon: 'ri-link',
      title: 'Short Link Generation & Tracking',
      subtitle: 'Track Every Click',
      description: 'Generate branded short links and track their performance across all your campaigns.',
      details: [
        'Short link generation for cleaner posts',
        'Custom branded domains (link.yourbrand.com)',
        'Click tracking and analytics for every link',
        'UTM parameter automation',
        'QR code generation for offline marketing',
        'Link expiration and password protection options',
        'Integration with Google Analytics',
        'A/B testing with link variants',
      ],
      color: 'from-[#22577A] to-[#e4f4ff]',
      bgColor: 'from-[#22577A]/10 to-[#e4f4ff]/20',
    },
  ];

  const benefits = [
    {
      icon: 'ri-stack-line',
      title: 'Built for Scale',
      description: 'Agencies use our built-in intelligent tools like best time to post and hashtag suggestions to manage more clients easily.',
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Agency-First Features',
      description: 'Get custom workflows, client approvals, and team collaboration tools made for agency teams.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'ROI You Can Show',
      description: 'Track campaign performance, monitor reach metrics, and create beautiful reports that showcase your impact against competitors.',
    },
    {
      icon: 'ri-time-line',
      title: 'Time-Saving Automation',
      description: 'Save your social team hours every month with smarter scheduling and bulk uploads.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Premium Support',
      description: 'Need expert social strategy and support? Our dedicated agency service team is here to help.',
    },
  ];

  const userTypes = [
    {
      icon: 'ri-user-star-line',
      title: 'Content Creators',
      description: 'Create, schedule, and optimize your content across all platforms',
      features: ['AI content generation', 'Cross-platform scheduling', 'Performance analytics'],
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Influencers',
      description: 'Manage your brand partnerships and grow your audience',
      features: ['Brand collaboration tools', 'Audience insights', 'Engagement tracking'],
    },
    {
      icon: 'ri-store-line',
      title: 'Business Owners',
      description: 'Build your brand and drive sales through social media',
      features: ['Sales tracking', 'Customer engagement', 'Marketing automation'],
    },
    {
      icon: 'ri-team-line',
      title: 'Marketing Agencies',
      description: 'Scale your agency with tools built for managing multiple clients',
      features: ['Client management', 'White-label reports', 'Team collaboration'],
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <SEO
        title="Features - Complete Social Media Management Tools"
        description="Explore MandaPost's powerful features: AI content creation, multi-platform scheduling, analytics & reporting, team collaboration, and more. Everything you need for social media success."
        keywords="social media features, AI content generation, social media scheduler, analytics dashboard, team collaboration, content calendar"
        canonicalUrl="https://mandapost.com/features"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#e4f4ff]/30 via-white to-[#b3d9ff]/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#66b3ff] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#b3d9ff] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/40 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-sparkling-line mr-2"></i>
              Powered by Advanced AI
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-bold text-[#22577A] mb-6 leading-tight">
              Features That Transform
              <span className="block bg-gradient-to-r from-[#38a3a5] to-[#66b3ff] bg-clip-text text-transparent">
                Your Social Media
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 mb-10 leading-relaxed">
              Discover the powerful tools and intelligent capabilities that make MandaPost the ultimate social media management platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                Start Free Trial
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-[#66b3ff] text-[#22577A] rounded-xl font-semibold text-lg hover:bg-[#e4f4ff]/20 transition-all cursor-pointer"
              >
                <i className="ri-calendar-line"></i>
                Schedule Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#22577A] mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools designed to streamline your workflow and maximize your social media impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br ${feature.bgColor} rounded-2xl border-2 border-[#66b3ff]/30 p-8 hover:border-[#66b3ff] hover:shadow-xl transition-all`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                  <i className={`${feature.icon} text-white text-3xl`}></i>
                </div>

                <h3 className="text-2xl font-bold text-[#22577A] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#38a3a5] font-semibold text-base mb-4">
                  {feature.subtitle}
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  {feature.description}
                </p>

                <div className="pt-4 border-t-2 border-[#66b3ff]/30">
                  <h4 className="font-semibold text-[#22577A] mb-4 flex items-center gap-2 text-sm">
                    <i className="ri-check-double-line text-[#66b3ff]"></i>
                    Key Capabilities:
                  </h4>
                  <ul className="space-y-2">
                    {feature.details.map((detail, dIndex) => (
                      <li key={dIndex} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#66b3ff] text-lg flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700 text-sm leading-relaxed">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {feature.link && (
                  <div className="mt-6">
                    <a
                      href={feature.link}
                      className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${feature.color} text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all cursor-pointer`}
                    >
                      <i className="ri-arrow-right-line"></i>
                      {feature.linkText}
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Analytics Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-bar-chart-box-line mr-2"></i>
              Performance Analytics
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#22577A] mb-6">
              Track Every Campaign, Beat Every Competitor
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Get real-time insights into your campaign performance and benchmark your success against competitors with our advanced analytics dashboard.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            {/* Campaign Analytics Card */}
            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl border-2 border-[#66b3ff]/30 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center">
                  <i className="ri-line-chart-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#22577A]">Campaign Performance</h3>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Track reach, engagement, conversions, and ROI for each campaign across all your social platforms in one unified dashboard.
              </p>

              {/* Sample Metrics */}
              <div className="bg-white rounded-xl p-6 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-[#22577A]">Spring Launch Campaign</h4>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                    Active
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <div className="text-xs text-gray-600 mb-1">Total Reach</div>
                    <div className="text-2xl font-bold text-[#22577A]">2.4M</div>
                    <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <i className="ri-arrow-up-line"></i>
                      +32% vs. last campaign
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <div className="text-xs text-gray-600 mb-1">Engagement Rate</div>
                    <div className="text-2xl font-bold text-[#22577A]">8.7%</div>
                    <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <i className="ri-arrow-up-line"></i>
                      +18% vs. industry avg
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <div className="text-xs text-gray-600 mb-1">Conversions</div>
                    <div className="text-2xl font-bold text-[#22577A]">1,247</div>
                    <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <i className="ri-arrow-up-line"></i>
                      +45% vs. target
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                    <div className="text-xs text-gray-600 mb-1">ROI</div>
                    <div className="text-2xl font-bold text-[#22577A]">340%</div>
                    <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <i className="ri-arrow-up-line"></i>
                      Exceeds goal by 140%
                    </div>
                  </div>
                </div>
              </div>

              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span className="text-sm">Track unlimited campaigns simultaneously</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span className="text-sm">Real-time updates every 5 minutes</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span className="text-sm">Custom KPIs and goal tracking</span>
                </li>
              </ul>
            </div>

            {/* Competitor Comparison Card */}
            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl border-2 border-[#66b3ff]/30 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center">
                  <i className="ri-trophy-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#22577A]">Competitor Analysis</h3>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Benchmark your performance against competitors and industry leaders to identify opportunities and stay ahead of the curve.
              </p>

              {/* Competitor Comparison */}
              <div className="bg-white rounded-xl p-6 mb-4">
                <h4 className="font-bold text-[#22577A] mb-4">Your Performance vs. Competitors</h4>

                <div className="space-y-4">
                  {/* Your Brand */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center text-white text-xs font-bold">
                          You
                        </div>
                        <span className="font-semibold text-[#22577A]">Your Brand</span>
                      </div>
                      <span className="font-bold text-[#22577A]">8.7%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] h-3 rounded-full" style={{width: '87%'}}></div>
                    </div>
                  </div>

                  {/* Competitor A */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          A
                        </div>
                        <span className="text-gray-700">Competitor A</span>
                      </div>
                      <span className="text-gray-600">6.2%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gray-400 h-3 rounded-full" style={{width: '62%'}}></div>
                    </div>
                  </div>

                  {/* Competitor B */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          B
                        </div>
                        <span className="text-gray-700">Competitor B</span>
                      </div>
                      <span className="text-gray-600">7.1%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gray-400 h-3 rounded-full" style={{width: '71%'}}></div>
                    </div>
                  </div>

                  {/* Industry Average */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 text-xs font-bold">
                          <i className="ri-bar-chart-line"></i>
                        </div>
                        <span className="text-gray-700">Industry Avg</span>
                      </div>
                      <span className="text-gray-600">5.5%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gray-300 h-3 rounded-full" style={{width: '55%'}}></div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 text-sm font-semibold">
                    <i className="ri-trophy-fill"></i>
                    You're outperforming the industry average by 58%!
                  </div>
                </div>
              </div>

              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span className="text-sm">Monitor up to 10 competitors</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span className="text-sm">Success rate comparisons across metrics</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span className="text-sm">Industry benchmark data included</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Analytics Features List */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#66b3ff] hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-dashboard-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-3">Unified Dashboard</h3>
              <p className="text-gray-700 leading-relaxed">
                See all your campaigns, platforms, and metrics in one comprehensive view. No more switching between tools.
              </p>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#66b3ff] hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-3">Automated Reports</h3>
              <p className="text-gray-700 leading-relaxed">
                Schedule weekly, monthly, or custom reports delivered directly to your inbox or clients' inboxes.
              </p>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#66b3ff] hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-download-cloud-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-3">Export Anywhere</h3>
              <p className="text-gray-700 leading-relaxed">
                Export your data to CSV, PDF, or connect to your favorite BI tools like Tableau and Power BI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Playground Showcase */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-3xl p-8 lg:p-12 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white font-medium mb-6">
                  <i className="ri-lightbulb-flash-line mr-2"></i>
                  Featured Tool
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-6">
                  The Playground: Your Daily Content Inspiration
                </h2>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Access a daily curated feed of generated content ideas updated every 24 hours. From trending hashtags to historical anniversaries, The Playground ensures you never run out of engaging post ideas. Discover viral trends, seasonal holidays, business milestones, and cultural moments—all analyzed by our AI to deliver the most relevant, timely content suggestions tailored to your brand and audience. Whether you're planning next week's content or need instant inspiration, The Playground keeps your social calendar fresh and engaging.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">247+</div>
                    <div className="text-white/80">Daily Content Ideas</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">5</div>
                    <div className="text-white/80">Content Categories</div>
                  </div>
                </div>

                <a
                  href="/playground"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
                >
                  <i className="ri-play-circle-line"></i>
                  Explore The Playground
                </a>
              </div>

              <div className="space-y-4">
                {/* Sample Content Cards */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
                  <div className="h-48 relative bg-gray-100">
                    <img
                      src="/assets/cupcake-celebration.jpg"
                      alt="National Cupcake Day"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-[#22577A] text-xs font-semibold uppercase">
                        Holiday
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-xs font-bold flex items-center gap-1">
                        <i className="ri-fire-fill"></i>
                        Trending
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-[#22577A] text-lg mb-2">National Cupcake Day</h3>
                    <p className="text-gray-700 text-sm mb-4">Sweet content ideas to celebrate today</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-green-600 font-semibold">
                        <i className="ri-arrow-up-line"></i>
                        +240%
                      </span>
                      <span className="text-gray-500">2.3M mentions</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80"
                        alt="ENIAC - First Computer"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">ENIAC (1945) - Eckert & Mauchly</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +310%
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400&q=80"
                        alt="Sustainability"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">Sustainability</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +290%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80"
                        alt="Infographics"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">Infographics</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +425%
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=400&q=80"
                        alt="Behind-The-Scenes"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">Behind-The-Scenes</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +380%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&q=80"
                        alt="User-Generated Content"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">User-Generated Content</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +340%
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&q=80"
                        alt="Mental Health Awareness"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">Mental Health Awareness</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +265%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&q=80"
                        alt="National Coffee Day"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">National Coffee Day</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +180%
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                    <div className="h-32 relative bg-gray-100">
                      <img
                        src="https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=400&q=80"
                        alt="Interactive Polls & Quizzes"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#22577A] text-sm mb-1">Interactive Polls</h4>
                      <span className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <i className="ri-arrow-up-line"></i>
                        +395%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Showcase */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
                <i className="ri-plug-line mr-2"></i>
                Seamless Integrations
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#22577A] mb-6">
                Connect All Your Favorite Platforms
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                MandaPost integrates with all major social media platforms, analytics tools, and productivity apps. Manage everything from one central hub.
              </p>
              <a
                href="/integrations"
                className="inline-flex items-center gap-2 text-[#22577A] font-semibold text-lg hover:text-[#38a3a5] transition-colors"
              >
                View All Integrations
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {[
                { name: 'Twitter/X', icon: 'ri-twitter-x-line', color: 'from-gray-700 to-black' },
                { name: 'Facebook', icon: 'ri-facebook-fill', color: 'from-blue-600 to-blue-700' },
                { name: 'Instagram', icon: 'ri-instagram-line', color: 'from-pink-500 to-purple-600' },
                { name: 'LinkedIn', icon: 'ri-linkedin-fill', color: 'from-blue-700 to-blue-800' },
                { name: 'TikTok', icon: 'ri-tiktok-line', color: 'from-gray-800 to-black' },
                { name: 'YouTube', icon: 'ri-youtube-fill', color: 'from-red-600 to-red-700' },
                { name: 'Pinterest', icon: 'ri-pinterest-fill', color: 'from-red-500 to-red-600' },
                { name: 'Google', icon: 'ri-google-fill', color: 'from-blue-500 to-green-500' },
                { name: 'More', icon: 'ri-add-circle-line', color: 'from-[#66b3ff] to-[#38a3a5]' },
              ].map((platform, index) => (
                <div
                  key={index}
                  className="aspect-square bg-white rounded-xl border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all flex flex-col items-center justify-center gap-2 cursor-pointer p-4"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${platform.color} rounded-lg flex items-center justify-center`}>
                    <i className={`${platform.icon} text-white text-2xl`}></i>
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center">{platform.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Calendar Planning Section */}
      <section className="py-20 bg-gradient-to-br from-[#f8fbff] via-[#e4f4ff] to-[#d0e9ff] relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#b3d9ff]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#66b3ff]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-5 py-2.5 bg-white/90 backdrop-blur-sm rounded-full text-[#22577A] font-semibold mb-6 shadow-md border border-[#b3d9ff]/30">
              <i className="ri-calendar-schedule-line mr-2 text-lg"></i>
              Visual Content Planning
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-[#22577A] mb-6 leading-tight">
              <span className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] bg-clip-text text-transparent">Plan Your Content</span>
              <span className="block text-3xl sm:text-4xl lg:text-5xl xl:text-7xl mt-3 font-extrabold">This Month</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Drag, drop, and organize your social content with our intuitive visual calendar. Watch your monthly plan come to life!
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Calendar Grid */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-200/50 backdrop-blur-sm relative z-0 overflow-hidden">
              {/* Subtle background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#e4f4ff]/10 via-white to-[#b3d9ff]/10 -z-10"></div>

              {/* Calendar Header - Month & Year */}
              <div className="mb-6 pb-4 border-b-2 border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-[#22577A]">February 2028</h3>
                    <p className="text-sm text-gray-600 mt-1">17 posts scheduled this month</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="w-10 h-10 rounded-lg bg-[#e4f4ff] hover:bg-[#b3d9ff] text-[#22577A] flex items-center justify-center transition-all">
                      <i className="ri-arrow-left-s-line text-xl"></i>
                    </button>
                    <button className="w-10 h-10 rounded-lg bg-[#e4f4ff] hover:bg-[#b3d9ff] text-[#22577A] flex items-center justify-center transition-all">
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-3 mb-3">
                {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day, i) => (
                  <div key={i} className="text-center text-xs font-bold text-[#22577A] py-2 uppercase tracking-wide">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-3">
                {Array.from({ length: 35 }, (_, i) => {
                  const dayNumber = i < 31 ? i + 1 : '';
                  // Days with scheduled posts
                  const scheduledDays = [1, 3, 4, 7, 9, 11, 13, 14, 16, 18, 20, 21, 22, 23, 27, 28, 30];
                  const hasPost = scheduledDays.includes(dayNumber as number);

                  // Multi-post days (days with more than one post)
                  const multiPostDays = [27, 28];
                  const hasMultiplePosts = multiPostDays.includes(dayNumber as number);

                  // Today's date (example: day 15)
                  const isToday = dayNumber === 15;

                  return (
                    <div
                      key={i}
                      className={`aspect-square rounded-xl border-2 p-3 text-sm font-semibold transition-all duration-200 relative group cursor-pointer ${
                        isToday
                          ? 'bg-gradient-to-br from-[#22577A] to-[#38a3a5] border-[#22577A] shadow-lg scale-105'
                          : hasPost
                          ? 'bg-gradient-to-br from-[#e4f4ff] to-white border-[#66b3ff] hover:shadow-lg hover:scale-105 hover:border-[#22577A]'
                          : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:bg-gray-100 hover:border-[#b3d9ff] hover:shadow-md'
                      }`}
                    >
                      <span className={`${isToday ? 'text-white' : hasPost ? 'text-[#22577A]' : 'text-gray-600'}`}>
                        {dayNumber}
                      </span>
                      {hasPost && !isToday && (
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
                          {hasMultiplePosts ? (
                            <>
                              <div className="w-1.5 h-3 bg-gradient-to-b from-[#38a3a5] to-[#22577A] rounded-full shadow-sm"></div>
                              <div className="w-1.5 h-3 bg-gradient-to-b from-[#66b3ff] to-[#38a3a5] rounded-full shadow-sm"></div>
                              <div className="w-1.5 h-3 bg-gradient-to-b from-[#b3d9ff] to-[#66b3ff] rounded-full shadow-sm"></div>
                            </>
                          ) : (
                            <div className="w-4 h-2 bg-gradient-to-r from-[#66b3ff] to-[#38a3a5] rounded-full shadow-sm"></div>
                          )}
                        </div>
                      )}
                      {isToday && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#b3d9ff] rounded-full border-2 border-white animate-pulse"></div>
                      )}
                      {/* Hover tooltip */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-end justify-center pb-1">
                        <div className="bg-[#22577A] text-white text-xs px-2 py-1 rounded-md shadow-lg whitespace-nowrap">
                          {hasPost ? (hasMultiplePosts ? '3 posts' : '1 post') : 'Add post'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Animated Polaroid Photos - In Front of Calendar */}
            <div className="absolute inset-0 pointer-events-none z-20">
              {/* Photo 1 - On Calendar Day 27 - Food */}
              <div
                className="absolute w-32 bg-white p-2 shadow-xl rounded-sm transform -rotate-12 animate-float"
                style={{
                  top: '395px',
                  left: '100px',
                  animation: 'float 6s ease-in-out infinite',
                  animationDelay: '0s'
                }}
              >
                <img
                  src="/assets/1.jpg"
                  alt="Food Post"
                  className="w-full h-24 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Photo 2 - On Calendar Day 27 - Event */}
              <div
                className="absolute w-32 bg-white p-2 shadow-xl rounded-sm transform rotate-6 animate-float"
                style={{
                  top: '400px',
                  left: '550px',
                  animation: 'float 7s ease-in-out infinite',
                  animationDelay: '1s'
                }}
              >
                <img
                  src="/assets/4.jpg"
                  alt="Event"
                  className="w-full h-24 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Photo 4 - Bottom Left - Person */}
              <div
                className="absolute w-28 bg-white p-2 shadow-xl rounded-sm transform rotate-12 animate-float"
                style={{
                  bottom: '-30px',
                  left: '-20px',
                  animation: 'float 6.5s ease-in-out infinite',
                  animationDelay: '3s'
                }}
              >
                <img
                  src="/assets/10.jpg"
                  alt="Lifestyle"
                  className="w-full h-20 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Photo 5 - Bottom Center - Flowers */}
              <div
                className="absolute w-32 bg-white p-2 shadow-xl rounded-sm transform -rotate-3 animate-float"
                style={{
                  bottom: '100px',
                  left: '50%',
                  transform: 'translateX(-50%) rotate(-3deg)',
                  animation: 'float 7.5s ease-in-out infinite',
                  animationDelay: '1.5s'
                }}
              >
                <img
                  src="/assets/3.jpg"
                  alt="Flowers"
                  className="w-full h-24 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Photo 6 - Right Side - Drinks */}
              <div
                className="absolute w-36 bg-white p-2 shadow-xl rounded-sm transform rotate-8 animate-float"
                style={{
                  top: '200px',
                  right: '-70px',
                  animation: 'float 8.5s ease-in-out infinite',
                  animationDelay: '2.5s'
                }}
              >
                <img
                  src="/assets/7.jpg"
                  alt="Drinks"
                  className="w-full h-28 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Photo 7 - Bottom Right - Product */}
              <div
                className="absolute w-32 bg-white p-2 shadow-xl rounded-sm transform -rotate-6 animate-float"
                style={{
                  bottom: '20px',
                  right: '-50px',
                  animation: 'float 6s ease-in-out infinite',
                  animationDelay: '4s'
                }}
              >
                <img
                  src="/assets/8.jpg"
                  alt="Product"
                  className="w-full h-24 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Photo 8 - On Calendar Day 28 - Pet (Postey!) */}
              <div
                className="absolute w-28 bg-white p-2 shadow-xl rounded-sm transform rotate-3 animate-float"
                style={{
                  top: '400px',
                  left: '350px',
                  animation: 'float 7s ease-in-out infinite',
                  animationDelay: '0.5s'
                }}
              >
                {/* EXAMPLE: Using Postey as a demo image - Replace with your own! */}
                <img
                  src="/assets/Postey_Mascot.jpg"
                  alt="Postey Mascot"
                  className="w-full h-20 object-cover mb-2 rounded-sm"
                />
                <p className="text-center text-xs font-semibold text-[#22577A]">Meet Postey!</p>
              </div>

              {/* Additional Photos ON TOP of Calendar - More Spread Out */}
              {/* Photo 11 - On Calendar Day 11 - Moved away from drag path */}
              <div
                className="absolute w-24 bg-white p-2 shadow-xl rounded-sm transform rotate-4 animate-float"
                style={{
                  top: '210px',
                  left: '480px',
                  animation: 'float 7s ease-in-out infinite',
                  animationDelay: '0.5s'
                }}
              >
                <img
                  src="/assets/2.jpg"
                  alt="Art"
                  className="w-full h-16 object-cover mb-1 rounded-sm"
                />
              </div>

              {/* Photo 13 - Above and right of Lifestyle */}
              <div
                className="absolute w-24 bg-white p-2 shadow-xl rounded-sm transform rotate-2 animate-float"
                style={{
                  bottom: '30px',
                  left: '60px',
                  animation: 'float 5.5s ease-in-out infinite',
                  animationDelay: '1.5s'
                }}
              >
                <img
                  src="/assets/9.jpg"
                  alt="Travel"
                  className="w-full h-16 object-cover mb-1 rounded-sm"
                />
              </div>

              {/* Photo 15 - On Calendar Day 22 */}
              <div
                className="absolute w-24 bg-white p-2 shadow-xl rounded-sm transform rotate-5 animate-float"
                style={{
                  top: '330px',
                  left: '480px',
                  animation: 'float 7.2s ease-in-out infinite',
                  animationDelay: '0.8s'
                }}
              >
                <img
                  src="/assets/5.jpg"
                  alt="Garden"
                  className="w-full h-16 object-cover mb-1 rounded-sm"
                />
              </div>

              {/* Photo 17 - On Calendar Day 28 */}
              <div
                className="absolute w-24 bg-white p-2 shadow-xl rounded-sm transform rotate-7 animate-float"
                style={{
                  top: '598px',
                  left: '280px',
                  animation: 'float 6.3s ease-in-out infinite',
                  animationDelay: '2.2s'
                }}
              >
                <img
                  src="/assets/12 copy.jpg"
                  alt="Coffee"
                  className="w-full h-16 object-cover mb-1 rounded-sm"
                />
              </div>

              {/* Dragging Photo Animation - Flower Market photo being dragged from day 8 to day 15 */}
              <div
                className="absolute w-28 bg-white p-2 shadow-2xl rounded-sm"
                style={{
                  animation: 'drag-photo 6s ease-in-out infinite',
                }}
              >
                <img
                  src="/assets/Untitled design (2).jpg"
                  alt="Flower Market"
                  className="w-full h-20 object-cover mb-2 rounded-sm"
                />
              </div>

              {/* Animated Cursor following the dragged photo */}
              <div
                className="absolute"
                style={{
                  animation: 'cursor-drag 6s ease-in-out infinite',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="drop-shadow-lg">
                  <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="#22577A" stroke="white" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Calendar Legend */}
          <div className="max-w-5xl mx-auto mt-8 bg-white rounded-2xl p-6 border border-gray-200/50 shadow-lg">
            <h4 className="text-sm font-bold text-[#22577A] mb-6 flex items-center gap-2 uppercase tracking-wide">
              <i className="ri-information-line text-lg"></i>
              Calendar Guide
            </h4>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#e4f4ff] to-white border-2 border-[#66b3ff] rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                  <div className="w-4 h-2 bg-gradient-to-r from-[#66b3ff] to-[#38a3a5] rounded-full"></div>
                </div>
                <div>
                  <div className="text-sm font-bold text-[#22577A] mb-1">Scheduled Post</div>
                  <div className="text-xs text-gray-600">Single post planned</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#e4f4ff] to-white border-2 border-[#66b3ff] rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-3 bg-gradient-to-b from-[#38a3a5] to-[#22577A] rounded-full"></div>
                    <div className="w-1.5 h-3 bg-gradient-to-b from-[#66b3ff] to-[#38a3a5] rounded-full"></div>
                    <div className="w-1.5 h-3 bg-gradient-to-b from-[#b3d9ff] to-[#66b3ff] rounded-full"></div>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-bold text-[#22577A] mb-1">Multiple Posts</div>
                  <div className="text-xs text-gray-600">3+ posts scheduled</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] border-2 border-[#22577A] rounded-xl flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0 relative">
                  15
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#b3d9ff] rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <div className="text-sm font-bold text-[#22577A] mb-1">Today</div>
                  <div className="text-xs text-gray-600">Current date</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl flex items-center justify-center text-gray-600 font-semibold shadow-sm flex-shrink-0">
                  2
                </div>
                <div>
                  <div className="text-sm font-bold text-[#22577A] mb-1">Available</div>
                  <div className="text-xs text-gray-600">No posts yet</div>
                </div>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-white/50 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-drag-drop-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Drag & Drop Planning</h3>
              <p className="text-gray-700">
                Effortlessly move posts around your calendar. Visual planning has never been this intuitive.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-white/50 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-[#38a3a5] to-[#66b3ff] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-gallery-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Visual Content Preview</h3>
              <p className="text-gray-700">
                See exactly how your feed will look before you publish. Perfect your aesthetic.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-white/50 shadow-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-[#66b3ff] to-[#b3d9ff] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Flexible Scheduling</h3>
              <p className="text-gray-700">
                Schedule posts weeks or months in advance. Your content calendar, your way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-20 bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/40 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-group-line mr-2"></i>
              Perfect For Everyone
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#22577A] mb-4">
              Whether You're a Content Creator, Influencer, or Business Owner...
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              MandaPost adapts to your unique needs and helps you achieve your social media goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {userTypes.map((userType, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border-2 border-[#66b3ff]/30 p-8 hover:border-[#66b3ff] hover:shadow-xl transition-all group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <i className={`${userType.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#22577A] mb-3">{userType.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{userType.description}</p>
                <ul className="space-y-2">
                  {userType.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-gray-600">
                      <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Additional User Types Callout */}
          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl border-2 border-[#66b3ff]/30 p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-[#22577A] mb-4">
                No matter your role or industry...
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                From solopreneurs to enterprise teams, from e-commerce brands to non-profits, MandaPost scales with your needs. Our AI-powered platform adapts to your workflow, helping you create better content, engage your audience, and measure your success—all from one powerful dashboard.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {['Freelancers', 'Startups', 'E-commerce', 'Non-Profits', 'Consultants', 'Coaches'].map((type, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-gradient-to-r from-[#e4f4ff] to-[#b3d9ff] text-[#22577A] rounded-full font-medium"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4">
              Why Choose MandaPost?
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Built for professionals who need powerful tools, seamless workflows, and measurable results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border-2 border-white/20 hover:bg-white/20 hover:border-white/40 transition-all group"
              >
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${benefit.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/90 leading-relaxed text-lg">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl border-2 border-[#66b3ff]/30 p-12 shadow-xl">
            <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-rocket-line text-white text-3xl"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#22577A] mb-4">
              Ready to Transform Your Social Media?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Join thousands of marketers who are already using MandaPost to streamline their workflow and boost engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                Get Started Free
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#66b3ff] text-[#22577A] rounded-xl font-semibold text-lg hover:bg-[#e4f4ff]/20 transition-all cursor-pointer"
              >
                <i className="ri-customer-service-line"></i>
                Talk to Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
